import React, { useEffect, useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { FaPython } from 'react-icons/fa';
import {
  FiCalendar,
  FiClock,
  FiCode,
  FiCpu,
  FiX,
} from 'react-icons/fi';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { API_BASE } from '../utils/apiEndpoints';
import {
  defineEditorThemes,
  readStoredCodeCustomThemeTokens,
  readStoredCodeEditorBackgroundMode,
  readStoredCodeSyntaxTheme,
  readStoredSqlCustomThemeTokens,
  readStoredSqlEditorBackgroundMode,
  readStoredSqlSyntaxTheme,
  resolveEditorTheme,
} from '../utils/monacoThemes.ts';
import {
  formatSubmissionDateTime,
  formatSubmissionLanguage,
  resolveSubmissionTestCounts,
  submissionStatusKind,
  submissionStatusLabel,
  type SubmissionHistoryItem,
} from '../utils/submissionStatus.ts';
import './css/SubmissionCodeModal.css';

export type SubmissionCodeModalProps = {
  submission: SubmissionHistoryItem;
  defaultTestCount?: number;
  locale: string;
  t: (key: string) => string;
  onClose: () => void;
};

type TeacherComment = {
  id: number;
  body: string;
  createdAt: string;
  class_id: number;
  class_name: string | null;
  author_role?: string;
  submission_id?: number | null;
};

function monacoLanguage(lang: string): string {
  const key = lang.trim().toLowerCase();
  if (key.includes('sql')) return 'sql';
  if (key === 'javascript') return 'javascript';
  return 'python';
}

function MetaStat({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
}) {
  return (
    <div className="submission-code-modal__meta-item">
      <span className="submission-code-modal__meta-icon">{icon}</span>
      <div className="submission-code-modal__meta-text">
        <span className="submission-code-modal__meta-value">{value}</span>
        <span className="submission-code-modal__meta-label">{label}</span>
      </div>
    </div>
  );
}

export const SubmissionCodeModal: React.FC<SubmissionCodeModalProps> = ({
  submission,
  defaultTestCount = 0,
  locale,
  t,
  onClose,
}) => {
  const kind = submissionStatusKind(submission.status);
  const statusText = submissionStatusLabel(submission.status, kind, t);
  const { passed, total } = resolveSubmissionTestCounts(submission, kind, defaultTestCount);
  const langLabel = formatSubmissionLanguage(submission.language);
  const isPython = submission.language.toLowerCase().includes('python');
  const editorLanguage = useMemo(() => monacoLanguage(submission.language), [submission.language]);
  const editorTheme = useMemo(
    () =>
      resolveEditorTheme(
        editorLanguage === 'sql',
        readStoredSqlSyntaxTheme(),
        readStoredCodeSyntaxTheme(),
      ),
    [editorLanguage],
  );
  const [teacherComments, setTeacherComments] = useState<TeacherComment[]>([]);
  const [teacherCommentsLoading, setTeacherCommentsLoading] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTeacherCommentsLoading(true);
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/submissions/${submission.id}/comments`, {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setTeacherComments(Array.isArray(data?.comments) ? data.comments : []);
        setReplyDraft('');
        setReplyError(null);
      } catch {
        if (!cancelled) setTeacherComments([]);
      } finally {
        if (!cancelled) setTeacherCommentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submission.id]);

  const handleBeforeMount = (monaco: Parameters<typeof defineEditorThemes>[0]) => {
    defineEditorThemes(
      monaco,
      readStoredSqlCustomThemeTokens(),
      readStoredCodeCustomThemeTokens(),
      readStoredSqlEditorBackgroundMode(),
      readStoredCodeEditorBackgroundMode(),
    );
  };

  useCloseOnEscape(true, onClose);

  const canReply = teacherComments.some((item) => item.author_role !== 'student');

  const sendReply = async () => {
    const body = replyDraft.trim();
    if (!body || replySending || !canReply) return;
    setReplySending(true);
    setReplyError(null);
    try {
      const res = await fetch(`${API_BASE}/api/submissions/${submission.id}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setReplyError(data?.error || t('problemPage.classContextReplyError'));
        return;
      }
      if (data?.comment) {
        setTeacherComments((prev) => [...prev, data.comment]);
      }
      setReplyDraft('');
    } catch {
      setReplyError(t('problemPage.classContextReplyError'));
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div className="submission-code-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="submission-code-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="submission-code-modal-title"
      >
        <header className="submission-code-modal__header">
          <div className="submission-code-modal__title-row">
            <h2 id="submission-code-modal-title" className="submission-code-modal__title">
              {t('problemPage.submissionCodeViewTitle')}
            </h2>
            <span className={`submission-code-modal__badge submission-code-modal__badge--${kind}`}>
              {statusText}
            </span>
          </div>
          <button
            type="button"
            className="submission-code-modal__close"
            onClick={onClose}
            aria-label={t('problemPage.close')}
          >
            <FiX strokeWidth={1.75} aria-hidden />
          </button>
        </header>

        <div className="submission-code-modal__meta">
          <div className="submission-code-modal__meta-item submission-code-modal__meta-item--wide">
            <FiCalendar className="submission-code-modal__meta-icon" strokeWidth={1.75} aria-hidden />
            <div className="submission-code-modal__meta-text">
              <span className="submission-code-modal__meta-value">
                {formatSubmissionDateTime(submission.submitted_at, locale)}
              </span>
              <span className="submission-code-modal__meta-label">ID: {submission.id}</span>
            </div>
          </div>

          <div className="submission-code-modal__meta-item">
            {isPython ? (
              <FaPython className="submission-code-modal__meta-icon submission-code-modal__meta-icon--brand" aria-hidden />
            ) : (
              <FiCode className="submission-code-modal__meta-icon" strokeWidth={1.75} aria-hidden />
            )}
            <div className="submission-code-modal__meta-text">
              <span className="submission-code-modal__meta-value">{langLabel}</span>
            </div>
          </div>

          {submission.execution_time != null ? (
            <MetaStat
              icon={<FiClock strokeWidth={1.75} aria-hidden />}
              value={`${Math.round(submission.execution_time)} ${t('problemPage.panelTimeMs')}`}
              label={t('problemPage.submissionCodeMetaTime')}
            />
          ) : null}

          {submission.memory_usage != null ? (
            <MetaStat
              icon={<FiCpu strokeWidth={1.75} aria-hidden />}
              value={`${Number(submission.memory_usage).toFixed(1)} ${t('problemPage.panelMemoryMb')}`}
              label={t('problemPage.submissionCodeMetaMemory')}
            />
          ) : null}

          {total > 0 ? (
            <MetaStat
              icon={<FiCode strokeWidth={1.75} aria-hidden />}
              value={`${passed} ${t('problemPage.submissionCodeMetaOf')} ${total}`}
              label={t('problemPage.submissionCodeMetaTests')}
            />
          ) : null}
        </div>

        <div className="submission-code-modal__body">
          <Editor
            height="360px"
            language={editorLanguage}
            value={submission.code || ''}
            theme={editorTheme}
            beforeMount={handleBeforeMount}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              wordWrap: 'off',
              renderLineHighlight: 'none',
              folding: true,
              contextmenu: false,
            }}
          />
        </div>

        <section className="submission-code-modal__feedback" aria-label={t('problemPage.teacherFeedbackTitle')}>
          <h3 className="submission-code-modal__feedback-title">{t('problemPage.teacherFeedbackTitle')}</h3>
          {teacherCommentsLoading ? (
            <p className="submission-code-modal__feedback-empty">{t('problemPage.teacherFeedbackLoading')}</p>
          ) : teacherComments.length === 0 ? (
            <p className="submission-code-modal__feedback-empty">{t('problemPage.teacherFeedbackEmpty')}</p>
          ) : (
            <ul className="submission-code-modal__feedback-list">
              {teacherComments.map((item) => (
                <li
                  key={item.id}
                  className={`submission-code-modal__feedback-item is-${item.author_role === 'student' ? 'student' : 'teacher'}`}
                >
                  <div className="submission-code-modal__feedback-meta">
                    <span>
                      {item.author_role === 'student'
                        ? t('problemPage.classContextYou')
                        : (item.class_name
                          ? t('problemPage.teacherFeedbackFromClass').replace('{name}', item.class_name)
                          : t('problemPage.classContextTeacher'))}
                    </span>
                    <span>
                      {formatSubmissionDateTime(item.createdAt, locale)}
                    </span>
                  </div>
                  <p className="submission-code-modal__feedback-body">{item.body}</p>
                </li>
              ))}
            </ul>
          )}
          {canReply ? (
            <form
              className="submission-code-modal__reply"
              onSubmit={(e) => {
                e.preventDefault();
                void sendReply();
              }}
            >
              <textarea
                className="submission-code-modal__reply-input"
                rows={2}
                maxLength={500}
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                placeholder={t('problemPage.classContextReplyPlaceholder')}
                disabled={replySending}
              />
              {replyError ? (
                <span className="submission-code-modal__reply-error">{replyError}</span>
              ) : null}
              <button
                type="submit"
                className="submission-code-modal__reply-send"
                disabled={replySending || !replyDraft.trim()}
              >
                {replySending
                  ? t('problemPage.classContextReplySending')
                  : t('problemPage.classContextReply')}
              </button>
            </form>
          ) : null}
        </section>
      </div>
    </div>
  );
};
